// Lib
import React from 'react';
import { Button as RomaButton, Checkbox } from '@milanote/roma';
import { EditorToolbar } from '@milanote/roma/application';

// Components
import ErrorToast from '../../../../../components/toast/ErrorToast';
import CancelDiscardButton from './CancelDiscardButton';

// Hooks
import { useTranslator } from '../../../../../components/i18n/useTranslator';
import { useImageModalV2BackgroundRemoval } from '../content/hooks/useImageModalV2BackgroundRemoval';

// Utils
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';

// Types
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import type { BackgroundRemovalProgress } from './imageModalBackgroundRemovalProgressTypes';

type ImageModalV2BackgroundToolbarProps = {
    element: ImMNElement;
    cropSourceImage: MNImageContent;
    largestImageUrl?: string;
    imageType: string;
    progress: BackgroundRemovalProgress | null;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    disabled: boolean;
    stagedActive: boolean;
    storedActive: boolean;
    onChangeStagedActive: (active: boolean) => void;
    onCancelClick: () => void;
    onSaveClick: () => void;
};

const ImageModalV2BackgroundToolbar: React.FC<ImageModalV2BackgroundToolbarProps> = ({
    element,
    cropSourceImage,
    largestImageUrl,
    imageType,
    progress,
    setProgress,
    disabled,
    stagedActive,
    storedActive,
    onChangeStagedActive,
    onCancelClick,
    onSaveClick,
}) => {
    const translate = useTranslator('popup', 'imageModal.editor');
    const imageModalTranslate = useTranslator('popup', 'imageModal');
    const elementId = getElementId(element);
    const hasStagedChanges = stagedActive !== storedActive;

    const { onToggle, errorMessage, clearError } = useImageModalV2BackgroundRemoval({
        element,
        elementId,
        cropSourceImage,
        largestImageUrl,
        imageType,
        setProgress,
        stagedActive,
        onChangeStagedActive,
    });

    return (
        <EditorToolbar.Shell>
            <EditorToolbar.List>
                {largestImageUrl && (
                    <Checkbox
                        size="sm"
                        variant="primary"
                        className="px-2.5 py-1.5"
                        label={imageModalTranslate('removeBackground')}
                        isSelected={stagedActive}
                        isDisabled={disabled || !!progress}
                        onChange={onToggle}
                    />
                )}

                <EditorToolbar.Divider type="spacer" />

                <div className="flex shrink-0 items-center gap-sm">
                    <CancelDiscardButton showDiscard={hasStagedChanges} onClick={onCancelClick} />

                    <RomaButton onClick={onSaveClick} isDisabled={disabled}>
                        {translate('save')}
                    </RomaButton>
                </div>
            </EditorToolbar.List>

            <ErrorToast show={!!errorMessage} message={errorMessage} groupId="ImageModalV2" onDismiss={clearError} />
        </EditorToolbar.Shell>
    );
};

export default ImageModalV2BackgroundToolbar;
